import React, { useEffect, useMemo, useState } from "react";
import "./MyBookingPage.css";
import MybookingCard from "./customComp/MybookingCard";
import MybookingCardSkeleton from "./customComp/MybookingCardSkeleton";
import axios from "axios";
import { API_BASE_URL } from "../utils";
import MyInvitesSection from "./MyInvitesSection";

import { HiOutlineArrowLeft } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import MyCarnival from "./MyCarnival";
import MyEvents from "./MyEvents";

const API_TABLE = `${API_BASE_URL}/master/APIs/ClubTable/bookingHistory`;
const API_PACKAGE = `${API_BASE_URL}/master/APIs/ClubPackage/bookingHistory`;

export default function MyBookingPage() {
  const navigate = useNavigate();
  const { tabName } = useParams(); // <-- from route param

  const TABS = ["Party Packages", "Table Booking", "Events", "My Invites", "Carnival"];

  // ---------------- User ----------------
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData")) || null;
    } catch {
      return null;
    }
  }, []);
  const userId = storedUser?.id ?? null;

  // ---------------- Slug Helpers ----------------
  const toSlug = (tab) => tab.replace(/\s+/g, "-");
  const fromSlug = (slug) =>
    TABS.find((t) => toSlug(t).toLowerCase() === slug?.toLowerCase()) || null;

  // ---------------- Active Tab ----------------
  const [activeTab, setActiveTab] = useState(
    tabName ? fromSlug(tabName) || TABS[0] : TABS[0]
  );

  useEffect(() => {
    if (tabName) {
      setActiveTab(fromSlug(tabName) || TABS[0]);
    } else {
      setActiveTab(TABS[0]); // default Party Packages when /my-bookings
    }
  }, [tabName]);

  // ---------------- States ----------------
  const [packageBookings, setPackageBookings] = useState([]);
  const [tableBookings, setTableBookings] = useState([]);
  const [upcomingPackageBookings, setUpcomingPackageBookings] = useState([]);
  const [upcomingTableBookings, setUpcomingTableBookings] = useState([]);

  const [loadingStates, setLoadingStates] = useState({
    package: false,
    table: false,
  });
  const [error, setError] = useState(null);

  // ---------------- Fetch Function ----------------
  const fetchBookings = async (type) => {
    if (!userId) return;

    setLoadingStates((prev) => ({ ...prev, [type]: true }));
    setError(null);

    const controller = new AbortController();
    const signal = controller.signal;

    const postForm = (url, status) => {
      const fd = new FormData();
      fd.append("user_id", userId);
      fd.append("type", status);
      return axios
        .post(url, fd, { signal })
        .then((r) => r?.data?.data ?? [])
        .catch((e) => {
          if (axios.isCancel(e)) return [];
          throw e;
        });
    };

    try {
      if (type === "table") {
        const [pending, completed] = await Promise.all([
          postForm(API_TABLE, "pending"),
          postForm(API_TABLE, "completed"),
        ]);
        setUpcomingTableBookings(pending);
        setTableBookings(completed);
      }
      if (type === "package") {
        const [pending, completed] = await Promise.all([
          postForm(API_PACKAGE, "pending"),
          postForm(API_PACKAGE, "completed"),
        ]);
        setUpcomingPackageBookings(pending);
        setPackageBookings(completed);
      }
    } catch (e) {
      console.error("Booking history fetch failed:", e);
      setError("Could not load bookings. Please try again.");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [type]: false }));
    }

    return () => controller.abort();
  };

  // ---------------- Load on Tab Change ----------------
  useEffect(() => {
    if (activeTab === "Party Packages") fetchBookings("package");
    if (activeTab === "Table Booking") fetchBookings("table");
  }, [activeTab, userId]);

  // ---------------- Helpers ----------------
  const renderSkeletons = (count = 3) =>
    Array.from({ length: count }).map((_, i) => (
      <MybookingCardSkeleton key={`s-${i}`} />
    ));

  const renderEmpty = (text = "No bookings found.") => (
    <div className="mbpswc-content-temp">
      <h2>{text}</h2>
    </div>
  );

  // ---------------- Tab Click ----------------
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === "Party Packages") {
      navigate("/my-bookings");
    } else {
      navigate(`/my-bookings/${toSlug(tab)}`);
    }
  };

  // ---------------- UI ----------------
  return (
    <div className="my-booking-Page">
      <div className="my-booking-Page-section">
        <div className="my-booking-Page-section-container">
          <div className="my-booking-Page-section-wrapper">
            <div className="club-review-header">
              <button
                onClick={() => navigate(-1)}
                className="club-review-back-btn"
              >
                <HiOutlineArrowLeft />
              </button>
              <h2>My Bookings</h2>
            </div>

            <div className="my-booking-Page-section-wrapper-content">
              {/* Tabs */}
              <div className="mbpswc-tabs">
                <div className="mbpswc-tabs-content">
                  {TABS.map((tab) => (
                    <div
                      key={tab}
                      onClick={() => handleTabClick(tab)}
                      className={
                        activeTab === tab
                          ? "mbpswc-tabs-content-tab-active"
                          : "mbpswc-tabs-content-tab"
                      }
                    >
                      {tab}
                    </div>
                  ))}
                </div>
              </div>

              {/* Login Check */}
              {!userId && (
                <div className="mbpswc-content-temp">
                  <h2>Please log in to view your bookings.</h2>
                </div>
              )}


              {/* Party Packages */}
              {userId && activeTab === "Party Packages" && (
                <div className="mbpswc-content">
                  <h1 className="mbpswc-content-title">
                    Upcoming ({upcomingPackageBookings.length})
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {loadingStates.package
                      ? renderSkeletons()
                      : upcomingPackageBookings.length === 0
                        ? renderEmpty("No upcoming package bookings.")
                        : upcomingPackageBookings.map((item, index) => (
                          <MybookingCard key={item?.id ?? index} data={item} />
                        ))}
                  </div>

                  <h1 className="mbpswc-content-title">
                    History ({packageBookings.length})
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {loadingStates.package
                      ? renderSkeletons()
                      : packageBookings.length === 0
                        ? renderEmpty("No past package bookings.")
                        : packageBookings.map((item, index) => (
                          <MybookingCard
                            key={item?.id ?? index}
                            data={item}
                            isHistory={true}
                          />
                        ))}
                  </div>
                </div>
              )}

              {/* Table Booking */}
              {userId && activeTab === "Table Booking" && (
                <div className="mbpswc-content">
                  <h1 className="mbpswc-content-title">
                    Upcoming ({upcomingTableBookings.length})
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {loadingStates.table
                      ? renderSkeletons()
                      : upcomingTableBookings.length === 0
                        ? renderEmpty("No upcoming table bookings.")
                        : upcomingTableBookings.map((item, index) => (
                          <MybookingCard key={item?.id ?? index} data={item} />
                        ))}
                  </div>

                  <h1 className="mbpswc-content-title">
                    History ({tableBookings.length})
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {loadingStates.table
                      ? renderSkeletons()
                      : tableBookings.length === 0
                        ? renderEmpty("No past table bookings.")
                        : tableBookings.map((item, index) => (
                          <MybookingCard
                            key={item?.id ?? index}
                            data={item}
                            isHistory={true}
                          />
                        ))}
                  </div>
                </div>
              )}

              {/* Events */}
              {activeTab === "Events" && (
                <div className="mbpswc-content-my-invites">
                  <MyEvents />

                </div>
              )}

              {/* My Invites */}
              {activeTab === "My Invites" && (
                <div className="mbpswc-content-my-invites">
                  <MyInvitesSection />
                </div>
              )}

              {/* My Carnival */}
              {activeTab === "Carnival" && (
                <div className="mbpswc-content-my-invites">
                  <MyCarnival />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
